import { useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, Users, Maximize, Building2, Sun } from "lucide-react";
import { STUDIOS, CONTACT } from "../data/site";
import { Reveal, MaskedLine } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useI18n } from "../i18n/LanguageContext";

export default function StudioPage() {
  const { t } = useI18n();
  const { slug } = useParams();
  const studio = STUDIOS.find((s) => s.slug === slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  if (!studio) return <Navigate to="/" replace />;
  const content = t(`studioData.${slug}`);
  const seasons = t("studio.seasons");

  const facts = [
    { icon: Users, label: t("studio.capacity"), value: content.capacity },
    { icon: Maximize, label: t("studio.surface"), value: studio.surface },
    { icon: Building2, label: t("studio.location"), value: content.floor },
    { icon: Sun, label: t("studio.orientation"), value: content.orientation },
  ];

  return (
    <div data-testid={`studio-page-${studio.slug}`}>
      <Seo titleKey={`seo.${studio.slug}.title`} descKey={`seo.${studio.slug}.description`} />
      <section ref={ref} className="relative h-[85svh] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={studio.image} alt={studio.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-pine-dark/60 via-black/20 to-pine-dark/70" />
        </motion.div>
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Link to="/" data-testid="studio-back-link" className="inline-flex items-center gap-2 text-cream/70 hover:text-wood text-sm mb-8 transition-colors">
              <ArrowLeft aria-hidden className="w-4 h-4" /> {t("studio.back")}
            </Link>
          </motion.div>
          <h1 className="font-serif text-cream tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.3}>{studio.name}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-4 font-serif italic text-xl text-wood-light"
          >
            {content.tagline}
          </motion.p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.25em] text-pine-light font-semibold mb-6">{t("studio.theStudio")}</p>
          <p className="text-lg md:text-xl leading-relaxed text-ink/80 font-serif">{content.description}</p>
          <div className="grid grid-cols-2 gap-6 mt-12">
            {facts.map((f) => (
              <div key={f.label} className="border-t border-pine/15 pt-4">
                <f.icon aria-hidden className="w-5 h-5 text-wood-dark mb-3" />
                <p className="text-xs uppercase tracking-[0.15em] text-ink/50">{f.label}</p>
                <p className="font-semibold text-pine mt-1">{f.value}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="md:col-span-5">
          <div className="bg-pine text-cream p-8 md:p-10 md:sticky md:top-28">
            <p className="text-xs uppercase tracking-[0.25em] text-wood font-semibold mb-6">{t("studio.amenitiesTitle")}</p>
            <ul className="space-y-4">
              {content.amenities.map((a) => (
                <li key={a.slice(0, 24)} className="flex items-start gap-3 text-cream/80 text-sm leading-relaxed">
                  <Check aria-hidden className="w-4 h-4 text-wood shrink-0 mt-0.5" /> {a}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-pine-light font-semibold mb-10">{t("studio.gallery")}</p>
        </Reveal>
        <div className="grid md:grid-cols-12 gap-6">
          {studio.gallery.map((img, i) => (
            <Reveal key={img.src} delay={i * 0.12} className={i === 0 ? "md:col-span-7" : "md:col-span-5"}>
              <div className="overflow-hidden group">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-pine-light font-semibold mb-6">{t("studio.pricingOverline")}</p>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tighter text-pine leading-[1.05]">
              {t("studio.pricingTitle1")}
              <span className="italic text-wood-dark">{t("studio.pricingTitle2a")}</span>
            </h2>
            <p className="mt-6 text-ink/60 leading-relaxed">
              {t("studio.pricingNote").replace("{phone}", CONTACT.phone)}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={`/contact?studio=${studio.slug}`}
                data-testid="studio-cta-contact"
                className="group inline-flex items-center gap-3 bg-pine text-cream px-7 py-4 text-sm font-semibold tracking-wide hover:bg-pine-light transition-colors duration-300"
              >
                {t("studio.ctaQuote")}
                <ArrowUpRight aria-hidden className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <a
                href={studio.airbnb}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="studio-cta-airbnb"
                className="group inline-flex items-center gap-3 border border-pine text-pine px-7 py-4 text-sm font-semibold tracking-wide hover:bg-pine hover:text-cream transition-colors duration-300"
              >
                {t("studio.ctaAirbnb")}
                <ArrowUpRight aria-hidden className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7">
            <table data-testid="studio-pricing-table" className="w-full text-left">
              <thead>
                <tr className="border-b border-pine/20 text-xs uppercase tracking-[0.15em] text-ink/50">
                  <th className="py-4 pr-4 font-semibold">{t("studio.season")}</th>
                  <th className="py-4 pr-4 font-semibold">{t("studio.week")}</th>
                  <th className="py-4 font-semibold">{t("studio.weekend")}</th>
                </tr>
              </thead>
              <tbody>
                {studio.pricing.map((p, i) => (
                  <tr key={seasons[i]} className="border-b border-pine/10 hover:bg-wood-light/20 transition-colors">
                    <td className="py-5 pr-4 text-ink/80">{seasons[i]}</td>
                    <td className="py-5 pr-4 font-serif text-xl text-pine">{p.week}</td>
                    <td className="py-5 font-serif text-xl text-pine">{p.weekend}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
